import { AppConfig } from '~/AppConfig'
import type { Variant as AlertVariant } from '~/components/shared/AlertTag'
import { AccountState, SubscriptionProductStatus } from '~/enums'

/*
 * Join classes
 */
export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

/*
 * Number to Currency
 */
export function currencyFormat(
  value: number,
  currency: string = 'EUR',
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 0,
  notation: 'compact' | 'engineering' | 'scientific' | 'standard' = 'standard',
): string {
  // Truncate the value to the desired decimal places
  const factor = Math.pow(10, maximumFractionDigits)
  const truncatedValue = Math.floor(value * factor) / factor

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: minimumFractionDigits, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: maximumFractionDigits, // (causes 2500.99 to be printed as $2,501)
    notation: notation,
  })

  return formatter.format(truncatedValue)
}

export function currencySymbol(currency: string): string {
  return (
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).formatToParts(1).find((x) => x.type === 'currency')
      ?.value ?? ''
  )
}

export function jwtDecode(token: string): { exp: number } | undefined {
  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
}

export function buildPspRedirectUrl(params: { [key: string]: any }, itemsArray: any): string {
  return Object.keys(params)
    .map((key) => {
      // 'NUVEI' case
      if (key === 'nuveiItems') {
        const items = itemsArray.map((item: any, index: number) => {
          return convertItemToQueryString(item, index)
        })

        return items.join('&')
      } else return encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
    })
    .join('&')
}

export function convertItemToQueryString(params: { [key: string]: any }, index: number): string {
  return Object.keys(params)
    .map((key) => {
      return encodeURIComponent(key) + `_${index + 1}` + '=' + encodeURIComponent(params[key])
    })
    .join('&')
}

export function getClientTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function setAccountStatusTextColor(status: string): string {
  switch (status) {
    case AccountState.CREATE_FAILED:
      return 'text-danger-600'
    case AccountState.INITIALIZED:
      return 'text-blue-800'
    case AccountState.CREATED:
      return 'text-blue-200'
    case AccountState.ACTIVE:
      return 'text-primary-300'
    case AccountState.PASSED:
      return 'text-success-750'
    case AccountState.UNDER_REVIEW:
    case AccountState.PASSED_UNDER_REVIEW:
      return 'text-yellow-200'
    case AccountState.PASSED_UPGRADED:
      return 'text-blue-400'
    case AccountState.BREACHED:
    case AccountState.PENDING_BREACHED:
      return 'text-danger-700'
    case AccountState.RESET:
      return 'text-blue-150'
    case AccountState.TRANSFERRED:
      return 'text-blue-300'
    default:
      return ''
  }
}

export function setKycStatusBgColor(kycStatusId: number): string {
  switch (kycStatusId) {
    case 10:
      return 'bg-gray-300'
    case 20:
      return 'bg-blue-100'
    case 30:
      return 'bg-yellow-100'
    case 40:
      return 'bg-success-300'
    case 50:
      return 'bg-danger-300'
    case 60:
      return 'bg-danger-400'
    default:
      return ''
  }
}

export function setKycStatusTextColor(kycStatusId: number): string {
  switch (kycStatusId) {
    case 10:
      return 'text-gray-700'
    case 20:
      return 'text-blue-400'
    case 30:
      return 'text-yellow-200'
    case 40:
      return 'text-success-750'
    case 50:
      return 'text-danger-600'
    case 60:
      return 'text-danger-700'
    default:
      return ''
  }
}

export function setPaymentAccountStatusTextColor(statusId: number): string {
  switch (statusId) {
    case 10: //new
      return 'text-blue-200'
    case 20: //submitted
      return 'text-blue-400'
    case 30: //in progress
      return 'text-yellow-200'
    case 40: //verified
      return 'text-success-750'
    case 50: //declined
      return 'text-danger-600'
    default:
      return ''
  }
}

export function getPaymentAccountType(statusId: number): AlertVariant {
  switch (statusId) {
    case 10: //new - draft
      return 'generic'
    case 20: //submitted - under review
    case 30: //in progress - under review
      return 'warning'
    case 40: //verified
      return 'success'
    case 50: //declined
      return 'error'
    default:
      return 'generic'
  }
}

export function setProductStatusTextColor(text: string): string {
  switch (text) {
    case SubscriptionProductStatus.ACTIVE:
      return 'text-primary-300'
    case SubscriptionProductStatus.EXPIRED:
      return 'text-danger-700'
    case SubscriptionProductStatus.PENDING:
      return 'text-yellow-200'
    case SubscriptionProductStatus.PENDING_APPROVAL:
      return 'text-gray-700'
    default:
      return ''
  }
}

// export const shortenString = (count: number) => (str: string | undefined) => {
//   if (!str) return
//   return str.length > count ? `..${str.slice(0, count)}` : str
// }

export const shortenString = (count: number) => (str: string | undefined) => {
  if (!str) return
  return str.length > count ? `··${str.slice(-count)}` : str
}

export const shortenPaymentAccount = shortenString(6)

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 * https://stackoverflow.com/a/14919494
 */
export const humanFileSize = (bytes: number, si = true, dp = 1) => {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }

  const units = si
    ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return bytes.toFixed(dp) + ' ' + units[u]
}

export function removeFirstSubdomain(url: string): string {
  try {
    // Create a URL object to parse the input URL
    const parsedUrl = new URL(url)
    const hostnameParts = parsedUrl.hostname.split('.')

    // Ensure the hostname has at least two parts (e.g., "subdomain.domain.com")
    if (hostnameParts.length > 2) {
      // Remove the first subdomain and join the remaining parts
      const newHostname = hostnameParts.slice(1).join('.')
      parsedUrl.hostname = newHostname
    }

    // Get the URL with the updated hostname as a string
    let resultUrl = parsedUrl.toString()

    // Remove trailing slash if it exists
    if (resultUrl.endsWith('/')) {
      resultUrl = resultUrl.slice(0, -1)
    }

    return resultUrl
  } catch (error) {
    console.error('Invalid URL:', error)
    return url // Return the original URL if there's an error
  }
}

export const buildTabTitle = (title: string | undefined): string => {
  return [title ?? AppConfig.TabDefaultPrefix, ...AppConfig.TabTitleSuffix].join(AppConfig.TabTitleSeparator)
}
